import React, { JSX, useEffect, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router'
import {
  Squares2X2Icon,
  LinkIcon,
  SignalIcon,
  PhotoIcon,
  CpuChipIcon,
  ChartBarIcon,
  CreditCardIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon
} from '@heroicons/react/24/outline'
import { LoadingPage } from 'components/Loading'
import { Avatar } from 'components/Avatar'
import Footer from 'components/Footer'
import { useAuth0 } from '@auth0/auth0-react'
import { useAuth, StreamToolsUser } from 'context/user'
import { clearStoredSocketToken } from 'context/socketToken'
import { useEntitlements } from 'context/plan'
import { useV1Visible, useStreamsVisibility } from 'hooks/useV1Visible'
import { useEventsList } from 'context/eventsList'
import { EventsSidebar } from 'components/EventsSidebar'
import { InEventSidebar } from 'components/InEventSidebar'
import { YouTubeBetaBanner } from 'components/YouTubeBetaBanner'
import { type EventListItem } from 'components/EventsSidebar'
import { eventSummaryToListItem } from 'lib/eventListItem'
import {
  ROOT_PATH,
  EVENTS_PATH,
  QUICK_OVERLAYS_PATH,
  CONNECTIONS_PATH,
  ASSETS_PATH,
  ADMIN_PATH,
  USAGE_PATH,
  BILLING_PATH,
  SETTINGS_PATH,
  ACCOUNT_PATH,
  NEW_EVENT_PATH,
  SUPPORT_PATH
} from 'lib/paths'
import classNames from 'classnames'

interface RailItem {
  name: string
  icon: React.ForwardRefExoticComponent<Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
    title?: string
    titleId?: string
  } & React.RefAttributes<SVGSVGElement>>
  href: string
  match: (pathname: string) => boolean
  superuserOnly?: boolean
  v1StaffOnly?: boolean
}

const RAIL_ITEMS: RailItem[] = [
  {
    name: 'Quick Overlays',
    icon: Squares2X2Icon,
    href: QUICK_OVERLAYS_PATH,
    match: (p) => p === QUICK_OVERLAYS_PATH || p.startsWith(QUICK_OVERLAYS_PATH)
  },
  {
    name: 'Connections',
    icon: LinkIcon,
    href: CONNECTIONS_PATH,
    match: (p) => p.startsWith(CONNECTIONS_PATH)
  },
  {
    name: 'Streams',
    icon: SignalIcon,
    href: EVENTS_PATH,
    match: (p) => p.startsWith('/streams') || p.startsWith('/events')
  },
  {
    name: 'Assets',
    icon: PhotoIcon,
    href: ASSETS_PATH,
    match: (p) => p.startsWith(ASSETS_PATH),
    v1StaffOnly: true
  },
  {
    name: 'Admin',
    icon: CpuChipIcon,
    href: ADMIN_PATH,
    match: (p) => p.startsWith(ADMIN_PATH),
    superuserOnly: true
  },
  {
    name: 'Usage',
    icon: ChartBarIcon,
    href: USAGE_PATH,
    match: (p) => p.startsWith(USAGE_PATH),
    superuserOnly: true
  }
]

const RAIL_ITEMS_PINNED: RailItem[] = [
  {
    name: 'Subscription',
    icon: CreditCardIcon,
    href: BILLING_PATH,
    match: (p) => p.startsWith(BILLING_PATH),
    v1StaffOnly: true
  },
  {
    name: 'Account',
    icon: UserCircleIcon,
    href: ACCOUNT_PATH,
    match: (p) => p.startsWith(SETTINGS_PATH)
  }
]

const IconRail = ({
  user,
  superuser,
  v1Visible,
  onLogout
}: {
  user: StreamToolsUser
  superuser: boolean
  v1Visible: boolean
  onLogout: () => void
}): JSX.Element => {
  const { pathname } = useLocation()

  const renderItem = ({ name, icon: Icon, href, match, superuserOnly }: RailItem): JSX.Element => {
    const active = match(pathname)
    return (
      <Link
        key={name}
        to={href}
        title={superuserOnly === true ? 'Superuser only — other users never see this' : name}
        data-testid={`rail-${name.toLowerCase().replace(/\s+/g, '-')}`}
        className={classNames(
          'flex w-20 flex-col items-center gap-1 rounded-md px-1 py-2 transition-colors',
          superuserOnly === true && 'border border-dashed border-brand/45',
          active
            ? 'bg-brand/15 text-brand'
            : 'text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary'
        )}
      >
        <Icon className='size-5' aria-hidden='true' />
        <span className='text-center text-[10px] font-medium leading-none'>{name}</span>
      </Link>
    )
  }

  return (
    <aside
      data-testid='icon-rail'
      className='flex h-screen w-24 flex-col items-center border-r border-white/5 bg-black py-4 sticky top-0'
    >
      <div className='flex flex-col items-center gap-y-6'>
        <Link to={ROOT_PATH} className='flex h-10 w-10 items-center justify-center'>
          <img alt='StagePilot' src='/images/logo-letters.png' className='h-7 w-auto' />
        </Link>
        <nav className='flex flex-col items-center gap-y-1'>
          {RAIL_ITEMS
            .filter((i) => i.superuserOnly !== true || superuser)
            .filter((i) => i.v1StaffOnly !== true || v1Visible)
            .map(renderItem)}
        </nav>
      </div>

      <div className='flex-1' aria-hidden='true' />

      <div className='flex flex-col items-center gap-y-2'>
        <nav className='mb-2 flex flex-col items-center gap-y-1'>
          {RAIL_ITEMS_PINNED
            .filter((i) => i.v1StaffOnly !== true || v1Visible)
            .map(renderItem)}
        </nav>
        <div
          data-testid='rail-user'
          title={`${user.name ?? ''}${user.email !== undefined ? ` — ${user.email}` : ''}`}
        >
          <Avatar
            name={user.name}
            email={user.email}
            picture={user.picture}
            className='h-9 w-9 text-xs'
          />
        </div>
        <button
          data-testid='rail-logout'
          onClick={onLogout}
          title='Sign out'
          className='flex h-10 w-10 items-center justify-center rounded-md text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary'
        >
          <ArrowRightOnRectangleIcon className='size-5' aria-hidden='true' />
          <span className='sr-only'>Sign out</span>
        </button>
      </div>
    </aside>
  )
}

const IN_EVENT_SIDEBAR_STORAGE_KEY = 'stream-tools.in-event-sidebar-open.v1'

const loadInEventSidebarOpen = (): boolean => {
  try {
    const raw = localStorage.getItem(IN_EVENT_SIDEBAR_STORAGE_KEY)
    if (raw === null) return true
    return raw === 'true'
  } catch {
    return true
  }
}

const saveInEventSidebarOpen = (open: boolean): void => {
  try {
    localStorage.setItem(IN_EVENT_SIDEBAR_STORAGE_KEY, String(open))
  } catch {

  }
}

const matchEventDetail = (pathname: string): string | null => {
  const m = pathname.match(/^\/events\/([^/]+)(?:\/|$)/)
  if (m == null) return null
  if (m[1] === 'new') return null
  return m[1]
}

const Layout = (): JSX.Element => {
  const { streamToolsUser, auth0Token } = useAuth()
  const { superuser, status: planStatus, limitFor } = useEntitlements()
  const v1Visible = useV1Visible()
  const streamsVisible = useStreamsVisibility() === 'visible'
  const { logout } = useAuth0()
  const { pathname } = useLocation()
  const { events, error: listError, refetch, currentStream } = useEventsList()

  const [inEventSidebarOpen, setInEventSidebarOpen] = useState<boolean>(() =>
    loadInEventSidebarOpen()
  )
  useEffect(() => {
    saveInEventSidebarOpen(inEventSidebarOpen)
  }, [inEventSidebarOpen])

  const loadedUserToken = (streamToolsUser !== null && auth0Token !== undefined)
  if (!loadedUserToken || streamToolsUser === null) return <LoadingPage />

  const handleLogout = (): void => {
    clearStoredSocketToken()
    void logout({ logoutParams: { returnTo: globalThis.location.origin } })
  }

  const currentEventId = matchEventDetail(pathname)
  const isInEventDetail = currentEventId !== null
  const showsStreamsSidebar =
    pathname.startsWith('/streams') || pathname.startsWith('/events')
  const streamsLocked = planStatus === 'ready' && limitFor('eventsPerAccount') === 0
  const sidebarEvents = events.map(eventSummaryToListItem)
  const slotMatches = currentStream != null && currentStream.event.eventId === currentEventId
  const listItem = sidebarEvents.find((e) => e.eventId === currentEventId) ?? null
  const slotItem = slotMatches ? eventSummaryToListItem(currentStream.event) : null
  const currentEventItem: EventListItem | null = isInEventDetail
    ? (listItem ?? slotItem)
    : null
  const staffView = superuser && slotMatches && !currentStream.viewerIsMember

  return (
    <div data-testid='layout-wrapper' className='flex min-h-screen bg-dm-bg-main text-dm-text-primary'>
      <IconRail user={streamToolsUser} superuser={superuser} v1Visible={v1Visible} onLogout={handleLogout} />

      {isInEventDetail && (
        inEventSidebarOpen
          ? (currentEventItem !== null
              ? <InEventSidebar
                  event={currentEventItem}
                  allEvents={sidebarEvents}
                  onClose={() => setInEventSidebarOpen(false)}
                  staffView={staffView}
                  listFailed={listError !== null}
                  onRetryList={() => { void refetch() }}
                />
              : <aside
                  data-testid='in-event-sidebar-loading'
                  className='flex h-screen w-[14.58rem] flex-col border-r border-white/5 bg-dm-bg-secondary sticky top-0'
                />)
          : <CollapsedSidebarToggle onOpen={() => setInEventSidebarOpen(true)} />
      )}
      {!isInEventDetail && showsStreamsSidebar && !streamsLocked && streamsVisible && (
        <EventsSidebar events={sidebarEvents} />
      )}

      <div className='flex min-w-0 flex-1 flex-col'>
        <YouTubeBetaBanner />
        <Outlet />
        <Footer contactHref={SUPPORT_PATH} />
      </div>
    </div>
  )
}

const CollapsedSidebarToggle = ({ onOpen }: { onOpen: () => void }): JSX.Element => (
  <aside
    data-testid='in-event-sidebar-collapsed'
    className='flex h-screen w-12 flex-col items-center border-r border-white/5 bg-dm-bg-secondary py-4 sticky top-0'
  >
    <button
      type='button'
      onClick={onOpen}
      title='Show sidebar'
      data-testid='in-event-sidebar-open'
      className='flex h-9 w-9 items-center justify-center rounded-md text-dm-text-accent hover:bg-white/5 hover:text-dm-text-primary'
    >
      <Bars3Icon className='size-5' aria-hidden='true' />
      <span className='sr-only'>Show sidebar</span>
    </button>
  </aside>
)

export default Layout

export { NEW_EVENT_PATH }
